import * as React from "react";

import "./text.css";

/**
 * Our text primitives, replacing `@salt-ds/core`'s Text family for the 178 `<Text>` elements
 * and their headings across 39 files.
 *
 * ── WHY THE CLASS NAMES SURVIVE ───────────────────────────────────────────────────────
 *
 * `saltText`, `saltText-h2`, `saltText-secondary` and friends are a CONTRACT, not vendor
 * residue: app CSS and tests select on them. Renaming them is a separate, mechanical change
 * where a mistake is visible; doing it here would hide a typography regression inside a
 * rename diff. Same call that was made for `saltFlexLayout` and `saltTooltip`.
 *
 * ── THE DEFAULTS THAT ARE LOAD-BEARING ────────────────────────────────────────────────
 *
 * Read from Salt's source rather than inferred, because inferring defaults is exactly how
 * the layout port lost 144px of content width:
 *
 *   as     -> "div"      Text is a BLOCK by default. Rendering a span instead collapses
 *                        every stacked caption onto one line.
 *   color  -> "primary"  Salt resolves `variant ?? color ?? "primary"`, so `saltText-primary`
 *                        is present on almost every text node in the app. Defaulting to
 *                        undefined drops the class and the colour falls to `currentColor`
 *                        — inherited from whatever ancestor happens to set one.
 *   disabled -> false
 *
 * `color="inherit"` is the one value that deliberately emits NO colour class.
 */
export type TextStyleAs =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "label"
  | "notation"
  | "display1"
  | "display2"
  | "display3"
  | "display4"
  | "code";

export type TextColor =
  | "primary"
  | "secondary"
  | "info"
  | "error"
  | "warning"
  | "success"
  | "inherit";

/**
 * ── WHY THESE TYPES ARE POLYMORPHIC ───────────────────────────────────────────────────
 *
 * `<Text as="label" htmlFor="x">` and `<Text as="a" href="/y">` must both typecheck, while
 * `<Text href>` on a plain div must not. That means the props depend on `as`, which is what
 * the helpers below express. `src/components/ui/label.tsx` consumes it directly as
 * `Omit<TextProps<"label">, "as">` to inherit `htmlFor`.
 *
 * The first attempt wrote this as `interface TextProps<T> extends
 * Omit<React.ComponentPropsWithoutRef<T>, "color">`, which produced 712 errors reading
 * "has no properties in common". An interface cannot extend an unresolved conditional type,
 * so the extends clause contributed nothing and every element lost `children` along with the
 * rest of its attributes. `PropsWithChildren` has to be applied explicitly and the element's
 * own attributes intersected rather than extended — the standard polymorphic shape, and the
 * one the vendor uses.
 */
type AsProp<T extends React.ElementType> = { as?: T };

type OwnProps = {
  disabled?: boolean;
  /** Clamps to N lines. Emits `--text-max-rows`, which `.saltText-lineClamp` reads. */
  maxRows?: number;
  styleAs?: TextStyleAs;
  color?: TextColor;
  /** The vendor's historical alias for `color`; it wins when both are given, as it did there. */
  variant?: TextColor;
};

export type TextProps<T extends React.ElementType = "div"> = React.PropsWithChildren<
  OwnProps & AsProp<T>
> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof (AsProp<T> & OwnProps)> & {
    ref?: React.ComponentPropsWithRef<T>["ref"];
  };

/**
 * The internal shape the implementations are written against. `forwardRef` cannot express a
 * component that is generic in its own props, so — exactly as the vendor does — the bodies are
 * written loosely and each export is cast to its polymorphic signature at the boundary.
 */
type AnyTextProps = OwnProps &
  Omit<React.HTMLAttributes<HTMLElement>, "color"> & {
    as?: React.ElementType;
  };

type PolymorphicText = <T extends React.ElementType = "div">(
  props: TextProps<T>,
) => React.ReactElement | null;

function classes(
  styleAs: TextStyleAs | undefined,
  color: TextColor,
  disabled: boolean,
  maxRows: number | undefined,
  className: string | undefined,
): string {
  const out = ["saltText"];
  if (disabled) out.push("saltText-disabled");
  if (maxRows) out.push("saltText-lineClamp");
  if (styleAs) out.push(`saltText-${styleAs}`);
  if (color !== "inherit") out.push(`saltText-${color}`);
  if (className) out.push(className);
  return out.join(" ");
}

const TextBase = React.forwardRef<HTMLElement, AnyTextProps>(function Text(
  { as, children, className, color: colorProp, disabled = false, maxRows, style, styleAs, variant, ...rest },
  ref,
) {
  const Component = (as ?? "div") as React.ElementType;
  const color = variant ?? colorProp ?? "primary";
  return (
    <Component
      className={classes(styleAs, color, disabled, maxRows, className)}
      {...rest}
      ref={ref}
      style={{ "--text-max-rows": maxRows, ...style } as React.CSSProperties}
    >
      {children}
    </Component>
  );
});

/**
 * Salt's headings pass ONLY `as`; they do not set `styleAs`. The element name is what the
 * stylesheet keys off (`h1.saltText`), so `<H2>` and `<Text as="h2">` are the same thing —
 * and `<Text styleAs="h2">` on a div is how the app gets heading LOOK without heading
 * SEMANTICS. All three paths have call sites here, so all three must keep working.
 */
function heading(tag: "h1" | "h2" | "h3" | "h4", displayName: string) {
  const Component = React.forwardRef<HTMLElement, AnyTextProps>(function Heading(props, ref) {
    return <Text as={tag} ref={ref} {...props} />;
  });
  Component.displayName = displayName;
  return Component;
}

/*
 * The polymorphic signature is APPLIED here, not merely declared. It was declared and left
 * unused for several ports, which meant `<Text as="a" href="...">` failed to typecheck — the
 * exported type was the loose internal one that knows nothing about anchors. Link is what
 * surfaced it.
 */
export const Text = TextBase as unknown as PolymorphicText;

export const H1 = heading("h1", "H1");
export const H2 = heading("h2", "H2");
export const H3 = heading("h3", "H3");
export const H4 = heading("h4", "H4");

export const Label = React.forwardRef<HTMLElement, AnyTextProps>(function Label(props, ref) {
  return <Text as="label" ref={ref} {...props} />;
});

function display(styleAs: TextStyleAs, displayName: string) {
  const Component = React.forwardRef<HTMLElement, AnyTextProps>(function Display(props, ref) {
    // `styleAs` first so a caller can still override it, matching Salt's `{...rest}` order.
    return <Text as="span" styleAs={styleAs} ref={ref} {...props} />;
  });
  Component.displayName = displayName;
  return Component;
}

export const Display1 = display("display1", "Display1");
export const Display2 = display("display2", "Display2");
export const Display3 = display("display3", "Display3");
export const Display4 = display("display4", "Display4");

export const Code = React.forwardRef<HTMLElement, AnyTextProps>(function Code(props, ref) {
  return <Text as="code" ref={ref} {...props} />;
});

export const TextNotation = React.forwardRef<HTMLElement, AnyTextProps>(function TextNotation(
  props,
  ref,
) {
  return <Text as="span" styleAs="notation" ref={ref} {...props} />;
});
